import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Alert,
    Heading1,
    IconCheckCircle,
    Stack,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import type {
    DigitallyReceivedPrescriberOrderInfoFragment,
    MailOrderProviderInfoFragment,
    PharmacyProviderInfoFragment,
    PrescriberInfoFragment,
    UnknownProviderInfoFragment,
} from '../../../../graphql/generated/graphql';
import { useDrugKey } from '../../../../hooks/use-drug-key';
import { useDrugName } from '../../../../hooks/use-drug-name';
import { useFlags } from '../../../../hooks/use-flags';
import type { NewDrugNoticeFlag } from '../../../../types/non-boolean-flags';
import { PrescriptionCancelledAlert } from '../../../common/PrescriptionCancelledAlert';
import { ShoppingHeader } from '../../../common/shopping-header/ShoppingHeader';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';

import { ProviderOverrides } from './ProviderOverrides';

export type OrderConfirmationHeaderProps = {
    prescriber: PrescriberInfoFragment;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    provider:
        | MailOrderProviderInfoFragment
        | PharmacyProviderInfoFragment
        | UnknownProviderInfoFragment;
};

export const OrderConfirmationHeader: FC<OrderConfirmationHeaderProps> = ({
    prescriber,
    prescriberOrder,
    provider,
}) => {
    const { t } = useTranslation('lilly');

    const { displayNewDrugNotice } = useFlags();

    const medication = getFragment(prescriberOrder.authorizedMedication);

    const drugKey = useDrugKey(medication);
    const drugName = useDrugName(medication);

    const showNewDrugNotice =
        drugKey &&
        (displayNewDrugNotice as NewDrugNoticeFlag).drugKeys.includes(drugKey);

    return (
        <>
            <ConditionalComponent condition={!!showNewDrugNotice}>
                <Alert severity="info">
                    {t('orderConfirmation.alert.newDrug', { drugName })}
                </Alert>
            </ConditionalComponent>
            <ShoppingHeader>
                <Stack align="center" gap="xxs">
                    <IconCheckCircle
                        filled
                        variant="rounded"
                        width="2em"
                        height="2em"
                        color="success"
                    />
                    <Heading1>{t('orderConfirmation.header')}</Heading1>
                </Stack>
            </ShoppingHeader>
            <PrescriptionCancelledAlert prescriberOrder={prescriberOrder} />
            <ProviderOverrides
                prescriber={prescriber}
                prescriberOrder={prescriberOrder}
                provider={provider}
            />
        </>
    );
};
